/**
 * AN ORDER'S TICKETS, AS PAPER.
 *
 * Ported from tkt-new-ui `app/boxoffice/_components/printTickets.ts` so a ticket reprinted at the
 * back office is the SAME ticket the till prints: same fields in the same slots, same QR (the
 * scan item's signature — what the gate resolves), same organiser-local date. A help desk handing a
 * fan a ticket that looks different from the one their neighbour bought at the till is a ticket
 * stewards will doubt.
 */

import { formatEventDay, formatClock } from './scheduleWindow';
import type { MasungTicketData } from '../masung-printer/printer';
import { getTicketVerifyUrl } from '../backendUrl';

/** A scan item = one admission. Only the fields the paper needs. */
export interface ScanItemLike {
  id: string;
  signature?: string | null;
  usedAt?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  ticketType?: { name?: string | null } | null;
  /** Where THIS ticket enters — on the ticket itself; the seat's section is only a fallback. */
  entrance?: { name?: string | null } | null;
  seat?: { seatNumber?: string | number | null; row?: { name?: string | null; section?: { name?: string | null; entrance?: { name?: string | null } | null } | null } | null } | null;
}

export function ticketFace(scanItem: ScanItemLike, order: any): MasungTicketData {
  const event = order?.event ?? {};
  const ref = String(scanItem.id).slice(-8).toUpperCase();
  const game = event.sportEvent?.games?.[0];
  const gameTime = game?.gameTime;
  const venue = event.address?.venue?.name || event.address?.address1 || '';
  return {
    eventName: event.title ?? '',
    subtitle: venue,
    attendeeName: [scanItem.firstName, scanItem.lastName].filter(Boolean).join(' ') || `${order?.firstName || ''} ${order?.lastName || ''}`.trim() || ref,
    ticketType: scanItem.ticketType?.name || '',
    // The ticket's own entrance first — what the back office shows and the emailed ticket uses.
    // Reading only the seat's section printed "-" for a ticket the system routes through Gate 1.
    gate: scanItem.entrance?.name || scanItem.seat?.row?.section?.entrance?.name || '',
    section: scanItem.seat?.row?.section?.name || '',
    row: scanItem.seat?.row?.name || '',
    seat: scanItem.seat?.seatNumber ?? '',
    date: formatEventDay(event.date, { day: 'numeric', month: 'short', year: 'numeric' }, 'en-GB') ?? '-',
    time: formatClock(gameTime ?? event.startTime),
    entryWindow: formatClock(event.startTime),
    venue,
    orderId: ref,
    // The SIGNATURE, not the row id — that is what the gate scanner resolves.
    qrData: `${getTicketVerifyUrl()}/v/${scanItem.signature || scanItem.id}`,
    homeTeam: game?.homeTeam?.name || '',
    awayTeam: game?.awayTeam?.name || '',
  };
}

/** A worthless ticket for checking layout — clearly marked, QR that resolves to nothing. */
export const sampleFace = (eventName = 'SAMPLE EVENT'): MasungTicketData => ({
  eventName, subtitle: 'TEST PRINT - NOT A TICKET', attendeeName: 'TEST', ticketType: 'Regular',
  gate: 'Gate 3', section: 'East', row: 'F', seat: 12,
  date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
  time: '4:00 PM', entryWindow: '2:00 PM', venue: '', orderId: 'TEST0001', qrData: 'TKT PRINTER TEST',
});
